import { Suspense, lazy, useEffect, useRef, useState } from "react";
import { BrowserRouter } from "react-router-dom";
import { MotionConfig } from "framer-motion";
import { AppRouter } from "@/router";
import { ThemeProvider } from "@/components/providers/theme-provider";
import { NativeLifecycle } from "@/components/providers/native-lifecycle";
import { GlobalAiConsentGate } from "@/components/ai-consent/global-ai-consent-gate";
import { AppUpdateGate } from "@/components/app-update/app-update-gate";
import { BrandSplash } from "@/components/layout/brand-splash";
import { useAuthStore } from "@/store/auth-store";
import { refreshAccessToken, isSessionDead } from "@/api/client";
import { installSessionSync } from "@/lib/session-sync";
import { isAccessTokenFresh } from "@/lib/jwt-exp";
import { isControlCenterHost } from "@/lib/tenant-host";
import { isAppLike, isPerfLite } from "@/lib/platform";

// Control Center only renders on `control.bytego.ai`; lazy-load it so tenant
// users never download that whole subtree in the main bundle.
const ControlCenterApp = lazy(() =>
  import("@/pages/control-center/app").then((m) => ({ default: m.ControlCenterApp }))
);

// ── bfcache sign-out guard ──────────────────────────────────────────────────
// Pairs with signOut() in the auth store. When a page is restored from the
// browser's back-forward cache (Back after sign-out), its JS heap is a frozen
// snapshot that can still look "authenticated" — so the route guards don't
// re-run and the dashboard reappears for a signed-out user. localStorage is
// the LIVE source of truth (not part of the frozen heap); if it shows no
// active session, force a full reload so the app boots fresh and the guards
// bounce the user to /login. Registered once at module load.
if (typeof window !== "undefined") {
  window.addEventListener("pageshow", (e) => {
    if (!(e as PageTransitionEvent).persisted) return;
    let authed = false;
    try {
      const raw = localStorage.getItem("campushubai-auth");
      authed = raw ? Boolean(JSON.parse(raw)?.state?.isAuthenticated) : false;
    } catch {
      authed = false;
    }
    if (!authed) window.location.reload();
  });
  // Every window of one session agrees on its tokens, and a sign-out in one
  // window reaches the others (lib/session-sync.ts). Registered once at
  // module load, alongside the bfcache guard above.
  installSessionSync();
}

// Validates the session against the server on every cold load.
// This prevents stale localStorage state (e.g. after a DB wipe) from mounting
// the AppShell with a dead token that fires 403s on every API call.
//
// We SKIP the refresh probe entirely when there is no persisted localStorage
// state to act on — a signed-out visitor never needs to ask the server.
function SessionRestorer({ children }: { children: React.ReactNode }) {
  const logout = useAuthStore((s) => s.logout);
  const setSessionRestored = useAuthStore((s) => s.setSessionRestored);
  // Always block rendering until the server has confirmed (or rejected) the session.
  const [ready, setReady] = useState(false);
  const attempted = useRef(false);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const hasPersistedSession =
      useAuthStore.getState().isAuthenticated
      || Boolean(useAuthStore.getState().token);

    if (!hasPersistedSession) {
      setSessionRestored(true);
      setReady(true);
      return;
    }

    // PERF: when we already have a persisted session, paint IMMEDIATELY and
    // let the refresh finish in the background.
    //
    // This gate used to block first paint on a full API round trip for every
    // logged-in load, on web and native alike — a cold TLS handshake plus the
    // request itself, so users stared at the splash/spinner for a few hundred
    // ms on EVERY open, not just the first. Nothing on screen actually needs
    // the refreshed token to render: the persisted store already has the user,
    // and the router only needs to know "is this person logged in".
    //
    // It is safe to let queries race the refresh because refreshAccessToken()
    // coalesces on a module-level `refreshPromise` (api/client.ts): any early
    // request that 401s on a stale token joins the very same in-flight
    // /auth/refresh through the axios interceptor and is then retried with the
    // fresh token. So this costs ONE refresh call, exactly as before — it does
    // not fan out into a burst that could trip the IP-keyed refresh rate limit.
    setSessionRestored(true);
    setReady(true);

    // A window that opens with an access token that is still good — the
    // meeting call's popup opened from a live session, a second tab, a
    // reload — does not need to spend a refresh to prove it: the first API
    // call it makes is the proof, and the 401 interceptor refreshes (and
    // signs out if the server says so) exactly as this probe would. Every
    // refresh skipped here is one fewer rotation the session's other windows
    // have to catch up with (2026-10-09: the join-time sign-outs).
    if (isAccessTokenFresh(useAuthStore.getState().token)) return;

    // Restore via refreshAccessToken() — the same helper the axios 401
    // interceptor uses — instead of a bare cookie-only probe. It sends the
    // durably-persisted refresh token from localStorage as a body fallback
    // AND the CSRF header, so a kiosk/POS-style device whose browser dropped
    // the httpOnly refresh cookie on close (or a cross-site SameSite=Lax
    // setup where the cookie never rides the refresh XHR) still rehydrates
    // from the token it saved at login. It applies the session internally.
    // (CAMP-2026-0115 — attendance kiosk auto-logout on reopen.)
    refreshAccessToken()
      .catch((err: unknown) => {
        // ONLY clear local state when the server actually rejected the session
        // (expired, user deleted, DB wiped…). A cold start with no network — a
        // phone opened in a lift, a kiosk booting before WiFi associates, an
        // API redeploy — must keep the persisted session so the app opens
        // logged-in and hydrates from cache. Logging out here was turning
        // every offline app-open into a forced re-login.
        if (isSessionDead(err)) {
          logout().catch(() => {});
        } else {
          console.warn("[SessionRestorer] session kept — refresh was inconclusive", err);
        }
      })
      .finally(() => {
        setSessionRestored(true);
        setReady(true);
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!ready) {
    if (isAppLike()) return <BrandSplash />;
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100dvh",
          gap: 16,
          background: "var(--color-bg, #f8fafc)",
        }}
      >
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: "50%",
            border: "3px solid var(--color-brand-border, #c7d2fe)",
            borderTopColor: "var(--color-brand, #6366f1)",
            animation: "nl-spin 0.7s linear infinite",
          }}
        />
      </div>
    );
  }

  return <>{children}</>;
}

export default function App() {
  // CAMP-2026-0579 — double-guard: main.tsx calls releasePreboot() right after
  // createRoot().render(), but in React 18 concurrent mode that fires before
  // the commit, so the overlay can linger one extra frame. This useEffect runs
  // after the first real paint and removes the preboot class / calls the hook
  // a second time (idempotent). Concretely: if `window.__CH_RELEASE_PREBOOT__`
  // is already gone (already called), this is a harmless no-op; if it is still
  // there the class removal on <html> closes the gap.
  useEffect(() => {
    const release = window.__CH_RELEASE_PREBOOT__;
    if (typeof release === "function") {
      release();
    } else {
      document.documentElement.classList.remove("ch-preboot");
      document.documentElement.removeAttribute("data-ch-preboot-theme");
    }
  }, []); // once on mount

  // Control Center lives on `control.bytego.ai` (and `control.localhost` in
  // dev). It's a completely separate surface from the tenant app — no
  // NativeLifecycle, no AppShell, its own login + dashboard tree. Branch
  // here so the tenant router never even mounts on that host.
  const isControlCenter = typeof window !== "undefined" && isControlCenterHost();

  // On the native app (and for reduce-motion users) tell framer-motion to
  // skip transform/layout animations globally. This stops the JS-driven
  // `repeat: Infinity` loops on dashboards etc. that CSS `animation: none`
  // can't reach — a major source of the app running hot.
  const reducedMotion = isPerfLite() ? "always" : "never";

  if (isControlCenter) {
    return (
      <MotionConfig reducedMotion={reducedMotion}>
        <ThemeProvider>
          <BrowserRouter>
            <SessionRestorer>
              <Suspense fallback={null}>
                <ControlCenterApp />
              </Suspense>
            </SessionRestorer>
          </BrowserRouter>
        </ThemeProvider>
      </MotionConfig>
    );
  }

  return (
    <MotionConfig reducedMotion={reducedMotion}>
      <ThemeProvider>
        <BrowserRouter>
          {/*
            NativeLifecycle wires up Capacitor's status bar, splash,
            back-button, deep links and push notifications. It mounts a
            single null-renderer inside Router context (needs `useNavigate`)
            and is a no-op on the web — safe to keep unconditional.
          */}
          <NativeLifecycle />
          {/* Force / optional native update gate — blocks the whole app
              (incl. login) when the installed build is below the server's
              minimum. No-op on web/PWA. */}
          <AppUpdateGate>
            <SessionRestorer>
              <GlobalAiConsentGate>
                <AppRouter />
              </GlobalAiConsentGate>
            </SessionRestorer>
          </AppUpdateGate>
        </BrowserRouter>
      </ThemeProvider>
    </MotionConfig>
  );
}
